import { FC, ReactNode, useState } from 'react'
import { Select } from 'antd'
import { SelectProps } from 'antd/lib/select'
import moment, { Moment } from 'moment-timezone'
import shallow from 'zustand/shallow'

import PerDatePicker from '../DatePicker'
import { EmployeeLevelEnum, EmployeeTagType, PyramidForecastDto, UserInfo } from '../../../types'
import { useForecastModalStore } from '.'
import { PerTableActionButtons } from '../TableActionButtons'
import PerFlexRow from '../FlexRow'
import PerButton from '../Button'
import { useTranslation } from 'react-i18next'
import { useEditForecastPermission } from '../../../modules/pyramids/usePermission'
import { usePyramidsStore, PyramidTabs } from '../../../modules/pyramids/PyramidsStore'
import { ForecastModalEmployeeTableFields } from '.'

type ForecastModalEmployeeTableDatePickerProps = {
  employeeId: string
  user: UserInfo
  width?: string
  dateColumnName: ForecastModalEmployeeTableFields
}

export const ForecastModalEmployeeTableDatePicker: FC<ForecastModalEmployeeTableDatePickerProps> = ({
  employeeId,
  user,
  width = '100%',
  dateColumnName,
}: ForecastModalEmployeeTableDatePickerProps) => {
  const { t } = useTranslation()
  const [employee, forecastDateValue, forecastEndDateValue, hasErrors, setEmployeeTableFormValues, levelChangeValue] =
    useForecastModalStore(
      (state) => [
        state.employeeImmutableData.find((employee: PyramidForecastDto) => employee.employeeId === employeeId),
        state.employeeTableFormValues[employeeId].currentValue.forecastDate,
        state.employeeTableFormValues[employeeId].currentValue.forecastEndDate,
        state.employeeTableFormValues[employeeId].hasErrors,
        state.setEmployeeTableFormValues,
        state.employeeTableFormValues[employeeId].currentValue.levelChange,
      ],
      shallow,
    )
  //state.employeeData.find((e) => e.employeeId === employeeId)!.forecastEndDate, // TODO Need to be refactored. Should use employeeTableFormValues but even changing IForecastModalEmployeeTableField it doesnt read forecastEndDate

  const [pyramidActiveTab, companyId] = usePyramidsStore((state) => [state.activeTab, state.selected.companyId], shallow)
  const hasPermission = useEditForecastPermission(companyId)
  const [opened, setOpened] = useState<boolean>(false)
  const handleOpened = (isOpen: boolean) => {
    if (isOpen !== opened) {
      setOpened(isOpen)
    }
  }

  const calcValue = () => {
    if (dateColumnName === 'forecastDate') {
      if (!!employee && !!employee.tags && !!employee.tags.includes(EmployeeTagType.Leaver)) {
        return employee.endDate
      } else {
        return forecastDateValue || ''
      }
    }

    if (dateColumnName === 'forecastEndDate') {
      return forecastEndDateValue || ''
    }
  }

  const isLowOrLeaving =
    !!employee &&
    !!employee.tags &&
    employee.tags.some((tag) => tag === EmployeeTagType.Low || tag === EmployeeTagType.Leaver || tag === EmployeeTagType.EntryLevelLow)

  const shouldForecastDateBeDisabled =
    dateColumnName === 'forecastDate' && forecastEndDateValue !== null && forecastEndDateValue !== undefined
  const shouldForecastEndDateBeDisabled =
    dateColumnName === 'forecastEndDate' &&
    ((forecastDateValue !== null && forecastDateValue !== undefined) || (levelChangeValue !== null && levelChangeValue !== undefined))

  return (
    <PerDatePicker
      open={opened}
      onOpenChange={handleOpened}
      allowClear
      showToday={false}
      inputDate={calcValue()}
      dateFormat="DMY"
      timeZoneAndLocale={user}
      size="default"
      width={width}
      disabled={
        !!isLowOrLeaving ||
        !hasPermission ||
        pyramidActiveTab === PyramidTabs.Age ||
        shouldForecastDateBeDisabled ||
        shouldForecastEndDateBeDisabled
      }
      disabledDate={(current: Moment | null) => !!current && current < moment()}
      onDateChanged={(date) => setEmployeeTableFormValues(employeeId, dateColumnName, date || null)}
      className={!!hasErrors && hasErrors === dateColumnName ? 'has-error' : ''}
      renderExtraFooter={() => (
        <PerFlexRow justify="center" align="middle" mt="xxs" mb="xxs">
          <PerButton
            text={t('pyramidsView.forecastModal.buttons.clear')}
            variant="pastel"
            color="error"
            onClick={() => {
              setEmployeeTableFormValues(employeeId, dateColumnName, null)
              setOpened(false)
            }}
          ></PerButton>
        </PerFlexRow>
      )}
    />
  )
}

type ForecastModalEmployeeTableSelectProps = {
  employeeId: string
  placeholder: string
  children: ReactNode
} & SelectProps<string>

export const ForecastModalEmployeeTableSelect: FC<ForecastModalEmployeeTableSelectProps> = ({
  employeeId,
  placeholder,
  children,
  ...restProps
}: ForecastModalEmployeeTableSelectProps) => {
  const [employee, value, hasErrors, setEmployeeTableFormValues, forecastEndDateValue] = useForecastModalStore(
    (state) => [
      state.employeeImmutableData.find((employee: PyramidForecastDto) => employee.employeeId === employeeId),
      state.employeeTableFormValues[employeeId].currentValue.levelChange,
      state.employeeTableFormValues[employeeId].hasErrors,
      state.setEmployeeTableFormValues,
      state.employeeTableFormValues[employeeId].currentValue.forecastEndDate,
    ],
    shallow,
  )

  const [pyramidActiveTab, companyId] = usePyramidsStore((state) => [state.activeTab, state.selected.companyId], shallow)

  const hasPermission = useEditForecastPermission(companyId)

  const calcValue = () => {
    if (!!employee && !!employee.tags && !!employee.tags.includes(EmployeeTagType.Leaver)) {
      return EmployeeLevelEnum.Leaver
    }
    return value || undefined
  }

  const isLowOrLeaving =
    !!employee &&
    !!employee.tags &&
    employee.tags.some((tag) => tag === EmployeeTagType.Low || tag === EmployeeTagType.Leaver || tag === EmployeeTagType.EntryLevelLow)

  return (
    <Select
      allowClear
      onChange={(value: string) => {
        setEmployeeTableFormValues(employeeId, 'levelChange', value || null)
      }}
      value={calcValue()}
      placeholder={placeholder}
      disabled={
        !!isLowOrLeaving ||
        !hasPermission ||
        pyramidActiveTab === PyramidTabs.Age ||
        (forecastEndDateValue !== null && forecastEndDateValue !== undefined)
      }
      className={!!hasErrors && hasErrors === 'levelChange' ? 'has-error' : ''}
      {...restProps}
    >
      {children}
    </Select>
  )
}

export const ForecastModalUndoButton: FC<{ employeeId: string }> = ({ employeeId }) => {
  const lastModifiedField = useForecastModalStore((state) => state.employeeTableFormValues[employeeId].lastModifiedField)
  const undoFormValues = useForecastModalStore((state) => state.undoFormValues)
  const { t } = useTranslation()

  return !!lastModifiedField ? (
    <PerTableActionButtons
      justify="start"
      buttons={[
        {
          showOnlyWhen: !!lastModifiedField,
          icon: 'redo_stroke',
          color: 'warning',
          onClick: () => undoFormValues(employeeId),
          additionalAttrs: {
            testid: 'fc-modal-empl-undo',
          },
          tooltip: t('pyramidsView.forecastModal.buttons.tooltipResetNewSettings'),
        },
      ]}
    />
  ) : null
}
